import { createTheme } from "@mui/material/styles";
import { tokens } from "./tokens";

const SYSTEM_FONTS = [
  "Times New Roman",
  "Georgia",
  "Garamond",
  "Arial",
  "Verdana",
  "Trebuchet MS",
  "Courier New",
  "Baskerville",
  "Cambria",
  "Didot",
];

const loadGoogleFont = (fontName: string) => {
  if (!fontName || SYSTEM_FONTS.includes(fontName)) return;
  const id = `dynamic-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
};

export const createAppTheme = (isColorChange: boolean, publishedBranding: any) => {
  if (publishedBranding?.fontFamily) {
    loadGoogleFont(publishedBranding.fontFamily);
  }

  const activeFontFamily = publishedBranding?.fontFamily
    ? `'${publishedBranding.fontFamily}', 'Roboto', sans-serif`
    : tokens.typography.fontFamily;

  console.log("isColorChange createAppTheme", isColorChange, publishedBranding);

  return createTheme({
    palette: {
      primary: {
        main: publishedBranding?.primaryColor
          ? publishedBranding.primaryColor
          : tokens.colors.connectedPurple,
      },
      secondary: { main: tokens.colors.modernBlue },
      success: { main: tokens.colors.innovativeGreen },

      background: {
        default: publishedBranding?.backgroundColor
          ? publishedBranding.backgroundColor
          : tokens.colors.connectedPurple,
        paper: tokens.colors.white,
      },

      text: {
        primary: tokens.colors.offBlack,
        secondary: tokens.colors.grey,
      },

      divider: tokens.colors.midGrey,
    },

    typography: {
      fontFamily: activeFontFamily,

      h1: { fontSize: 58, fontWeight: 900 },
      h2: { fontSize: 46, fontWeight: 900 },
      h3: { fontSize: 36, fontWeight: 900 },
      h4: { fontSize: 30, fontWeight: 900 },
      h5: { fontSize: 24, fontWeight: 900 },
      h6: { fontSize: 20, fontWeight: 800 },

      body1: { fontSize: 18, fontWeight: 500 },
      body2: { fontSize: 14, fontWeight: 500 },

      button: {
        fontWeight: 800,
        textTransform: "none",
      },
    },

    shape: {
      borderRadius: tokens.radius.card,
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: tokens.colors.offWhite,
            fontFamily: `${activeFontFamily} !important`,
            '--tn-font-family': activeFontFamily,
            // Bridge MUI theme primary color to CSS custom properties for PrimeReact components
            '--tn-primary-color': publishedBranding?.primaryColor || tokens.colors.connectedPurple,
            '--tn-primary-color-hover': publishedBranding?.primaryColor
              ? `color-mix(in srgb, ${publishedBranding.primaryColor} 80%, white)`
              : `color-mix(in srgb, ${tokens.colors.connectedPurple} 80%, white)`,
            '--tn-background-color': tokens.colors.offWhite,
          },
          '.p-radiobutton:not(.p-radiobutton-disabled) .p-radiobutton-box:hover, .p-radiobutton:not(.p-radiobutton-disabled):has(.p-radiobutton-box:hover)': {
            borderColor: 'var(--tn-primary-color) !important',
          },
          '.p-radiobutton .p-radiobutton-box.p-highlight': {
            borderColor: 'var(--tn-primary-color) !important',
            background: 'var(--tn-primary-color) !important',
          },
          '.p-radiobutton .p-radiobutton-box.p-highlight:not(.p-disabled):hover': {
            borderColor: 'var(--tn-primary-color-hover) !important',
            background: 'var(--tn-primary-color-hover) !important',
          },
          '.p-radiobutton .p-radiobutton-box:not(.p-disabled).p-focus, .p-radiobutton.p-focus .p-radiobutton-box': {
            boxShadow: '0 0 0 0.2rem color-mix(in srgb, var(--tn-primary-color) 20%, transparent) !important',
            borderColor: 'var(--tn-primary-color) !important',
          },
          '.p-calendar:not(.p-calendar-disabled).p-focus > .p-inputtext, .p-calendar:not(.p-calendar-disabled) .p-inputtext:focus': {
            borderColor: 'var(--tn-primary-color) !important',
            boxShadow: '0 0 0 0.2rem color-mix(in srgb, var(--tn-primary-color) 20%, transparent) !important',
          },
          '.p-calendar:not(.p-calendar-disabled) .p-inputtext:hover': {
            borderColor: 'var(--tn-primary-color) !important',
          },
          '.p-inputtext:enabled:focus': {
            borderColor: 'var(--tn-primary-color) !important',
            boxShadow: '0 0 0 0.2rem color-mix(in srgb, var(--tn-primary-color) 20%, transparent) !important',
          },
          '.p-inputtext:enabled:hover': {
            borderColor: 'var(--tn-primary-color) !important',
          },
          '.p-checkbox .p-checkbox-box.p-highlight': {
            borderColor: 'var(--tn-primary-color) !important',
            background: 'var(--tn-primary-color) !important',
          },
          '.p-multiselect-panel .p-multiselect-items .p-multiselect-item:not(.p-highlight):not(.p-disabled):hover, .p-multiselect-panel .p-multiselect-items .p-multiselect-item:not(.p-highlight).p-focus': {
            background: 'color-mix(in srgb, var(--tn-primary-color) 8%, white) !important',
            color: '#212121 !important',
          },
          '.p-multiselect-panel .p-multiselect-items .p-multiselect-item.p-highlight': {
            background: 'color-mix(in srgb, var(--tn-primary-color) 14%, white) !important',
            color: '#212121 !important',
          },
          '.p-multiselect-panel .p-multiselect-items .p-multiselect-item.p-highlight:hover': {
            background: 'color-mix(in srgb, var(--tn-primary-color) 20%, white) !important',
          },
          '.p-dropdown:not(.p-disabled):hover': {
            borderColor: 'var(--tn-primary-color) !important',
          },
          '.p-dropdown:not(.p-disabled).p-focus': {
            borderColor: 'var(--tn-primary-color) !important',
            boxShadow: '0 0 0 0.2rem color-mix(in srgb, var(--tn-primary-color) 25%, transparent) !important',
          },
          '.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover, .p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight).p-focus': {
            background: 'color-mix(in srgb, var(--tn-primary-color) 8%, white) !important',
            color: '#212121 !important',
          },
          '.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight': {
            background: 'color-mix(in srgb, var(--tn-primary-color) 14%, white) !important',
            color: 'var(--tn-primary-color) !important',
          },
          '.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight:hover': {
            background: 'color-mix(in srgb, var(--tn-primary-color) 20%, white) !important',
            color: 'var(--tn-primary-color) !important',
          },
          '.p-datatable .p-datatable-tbody > tr > td .p-row-editor-init, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-save, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-cancel': {
            color: 'var(--tn-primary-color) !important',
            borderRadius: '50% !important',
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease !important',
          },
          '.p-datatable .p-datatable-tbody > tr > td .p-row-editor-init:hover, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-save:hover, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-cancel:hover': {
            color: 'var(--tn-primary-color) !important',
            backgroundColor: 'color-mix(in srgb, var(--tn-primary-color) 14%, transparent) !important',
            boxShadow: 'none !important',
          },
          '.p-datatable .p-datatable-tbody > tr > td .p-row-editor-init:focus, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-save:focus, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-cancel:focus, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-init:focus-visible, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-save:focus-visible, .p-datatable .p-datatable-tbody > tr > td .p-row-editor-cancel:focus-visible': {
            color: 'var(--tn-primary-color) !important',
            backgroundColor: 'color-mix(in srgb, var(--tn-primary-color) 18%, transparent) !important',
            boxShadow: '0 0 0 0.2rem color-mix(in srgb, var(--tn-primary-color) 25%, transparent) !important',
            outline: 'none !important',
          },
          '.fc.fc-theme-standard .fc-toolbar .fc-button-group .fc-button, .fc .fc-toolbar .fc-button-group .fc-button, .fc .fc-button, .fc .fc-button-primary': {
            backgroundColor: '#ffffff !important',
            color: 'var(--tn-primary-color) !important',
            border: '1px solid var(--tn-primary-color) !important',
            boxShadow: 'none !important',
            fontWeight: '700 !important',
            fontSize: '14px !important',
            fontFamily: 'Area Normal, Inter, sans-serif !important',
            minWidth: '36px !important',
            display: 'inline-flex !important',
            alignItems: 'center !important',
            justifyContent: 'center !important',
          },
          '.fc .fc-prev-button, .fc .fc-next-button': {
            fontSize: '0 !important',
            lineHeight: '0 !important',
            minWidth: '38px !important',
            width: '38px !important',
            height: '38px !important',
            padding: '0 !important',
            display: 'inline-flex !important',
            alignItems: 'center !important',
            justifyContent: 'center !important',
            userSelect: 'none !important',
          },
          '.fc.fc-theme-standard .fc-toolbar .fc-button-group .fc-button:not(:disabled):hover, .fc .fc-toolbar .fc-button-group .fc-button:not(:disabled):hover, .fc .fc-button:not(:disabled):hover, .fc .fc-button-primary:not(:disabled):hover, .fc .fc-prev-button:not(:disabled):hover, .fc .fc-next-button:not(:disabled):hover': {
            backgroundColor: 'var(--tn-primary-color) !important',
            color: '#ffffff !important',
            borderColor: 'var(--tn-primary-color) !important',
          },
          '.fc.fc-theme-standard .fc-toolbar .fc-button-group .fc-button.fc-button-active, .fc .fc-toolbar .fc-button-group .fc-button.fc-button-active, .fc .fc-button-active, .fc .fc-button-primary.fc-button-active': {
            backgroundColor: 'var(--tn-primary-color) !important',
            color: '#ffffff !important',
            borderColor: 'var(--tn-primary-color) !important',
          },
          '.fc .fc-prev-button .fc-icon, .fc .fc-next-button .fc-icon': {
            display: 'none !important',
          },
          '.p-datepicker table td > span.p-highlight': {
            color: 'var(--tn-primary-color) !important',
            background: 'color-mix(in srgb, var(--tn-primary-color) 14%, white) !important',
          },
          '.p-datepicker:not(.p-disabled) table td span.p-highlight:hover': {
            color: 'var(--tn-primary-color) !important',
            background: 'color-mix(in srgb, var(--tn-primary-color) 22%, white) !important',
          },
          '.p-datepicker:not(.p-disabled) table td span:not(.p-highlight):not(.p-disabled):hover': {
            background: 'color-mix(in srgb, var(--tn-primary-color) 10%, white) !important',
            color: 'var(--tn-primary-color) !important',
          },
          '.p-datepicker table td.p-datepicker-today > span': {
            background: '#f0f0f0',
            color: 'var(--tn-primary-color)',
          },
          '.p-datepicker table td.p-datepicker-today > span.p-highlight': {
            color: 'var(--tn-primary-color) !important',
            background: 'color-mix(in srgb, var(--tn-primary-color) 14%, white) !important',
          },
          '.p-datepicker .p-datepicker-header .p-datepicker-prev, .p-datepicker .p-datepicker-header .p-datepicker-next, .p-datepicker .p-datepicker-header button.p-datepicker-prev, .p-datepicker .p-datepicker-header button.p-datepicker-next': {
            color: 'var(--tn-primary-color) !important',
            borderRadius: '50% !important',
          },
          '.p-datepicker .p-datepicker-header .p-datepicker-prev .p-datepicker-prev-icon, .p-datepicker .p-datepicker-header .p-datepicker-next .p-datepicker-next-icon, .p-datepicker .p-datepicker-header .p-datepicker-prev svg, .p-datepicker .p-datepicker-header .p-datepicker-next svg': {
            color: 'var(--tn-primary-color) !important',
          },
          '.p-datepicker .p-datepicker-header .p-datepicker-prev:hover, .p-datepicker .p-datepicker-header .p-datepicker-next:hover, .p-datepicker .p-datepicker-header button.p-datepicker-prev:hover, .p-datepicker .p-datepicker-header button.p-datepicker-next:hover, .p-datepicker .p-datepicker-header .p-datepicker-title .p-datepicker-month:hover, .p-datepicker .p-datepicker-header .p-datepicker-title .p-datepicker-year:hover': {
            color: 'var(--tn-primary-color) !important',
            background: 'color-mix(in srgb, var(--tn-primary-color) 14%, transparent) !important',
          },
          '.p-datepicker .p-datepicker-header .p-datepicker-prev:focus, .p-datepicker .p-datepicker-header .p-datepicker-next:focus, .p-datepicker .p-datepicker-header .p-datepicker-prev:focus-visible, .p-datepicker .p-datepicker-header .p-datepicker-next:focus-visible, .p-datepicker .p-datepicker-header .p-datepicker-prev:active, .p-datepicker .p-datepicker-header .p-datepicker-next:active, .p-datepicker .p-datepicker-header button.p-datepicker-prev:focus, .p-datepicker .p-datepicker-header button.p-datepicker-next:focus, .p-datepicker .p-datepicker-header button.p-datepicker-prev:active, .p-datepicker .p-datepicker-header button.p-datepicker-next:active': {
            color: 'var(--tn-primary-color) !important',
            background: 'color-mix(in srgb, var(--tn-primary-color) 18%, transparent) !important',
            boxShadow: '0 0 0 0.2rem color-mix(in srgb, var(--tn-primary-color) 25%, transparent) !important',
            outline: 'none !important',
          },
          '.p-datepicker table td > span:focus, .p-datepicker table td > span:focus-visible': {
            boxShadow: '0 0 0 0.2rem color-mix(in srgb, var(--tn-primary-color) 25%, transparent) !important',
          },
          '.p-datepicker .p-monthpicker .p-monthpicker-month.p-highlight, .p-datepicker .p-yearpicker .p-yearpicker-year.p-highlight': {
            color: 'var(--tn-primary-color) !important',
            background: 'color-mix(in srgb, var(--tn-primary-color) 14%, white) !important',
          },
        },
      },

      // Unified form input styling
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: '48px',
            borderRadius: '10px',
            fontFamily: activeFontFamily,
            fontSize: '16px',
            fontWeight: 400,
            backgroundColor: tokens.colors.offWhite,

            // Override for Select components to have pill shape
            '&.MuiSelect-root': {
              borderRadius: tokens.radius.pill,
              backgroundColor: tokens.colors.offWhite,
            },

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.colors.midGrey,
              borderWidth: '1px',
            },

            // Override for Select components
            '&.MuiSelect-root .MuiOutlinedInput-notchedOutline': {
              borderRadius: tokens.radius.pill,
            },

            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '1px',
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },

            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.colors.midGrey,
              borderWidth: '1px',
            },

            '&.Mui-disabled': {
              backgroundColor: tokens.colors.offWhite,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.colors.offWhite,
              },
            },

            '& input': {
              padding: '12px 14px',
              color: tokens.colors.offBlack,
              '&::placeholder': {
                color: tokens.colors.grey,
                opacity: 1,
              },
            },

            // Brand primary text while typing
            '&.Mui-focused input': {
              color: theme.palette.primary.main,
            },
          }),

          // Textarea specific
          multiline: ({ theme }) => ({
            height: 'auto',
            minHeight: '120px',

            '& textarea': {
              padding: '12px 14px',
              resize: 'vertical',
              color: tokens.colors.offBlack,
            },

            '&.Mui-focused textarea': {
              color: theme.palette.primary.main,
            },
          }),
        },
      },

      MuiSelect: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: '48px',
            borderRadius: tokens.radius.pill,
            fontFamily: 'Area Normal, Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 400,
            backgroundColor: tokens.colors.offWhite,

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.colors.midGrey,
              borderWidth: '1px',
              borderRadius: tokens.radius.pill,
            },

            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '1px',
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },

            '&.Mui-disabled': {
              backgroundColor: tokens.colors.offWhite,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.colors.offWhite,
              },
            },
          }),

          select: ({ theme }) => ({
            padding: '12px 14px',
            color: tokens.colors.offBlack,

            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
          }),

          icon: {
            color: tokens.colors.grey,
            right: '12px',
            '& .MuiSvgIcon-root': {
              transform: 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out',
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            '&.MuiMenu-paper': {
              borderRadius: '10px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              marginTop: '4px',
            },
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: 'Area Normal, Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 400,
            padding: '12px 16px',

            '&:hover': {
              backgroundColor: tokens.colors.offWhite,
            },

            '&.Mui-selected': {
              backgroundColor: 'color-mix(in srgb, var(--tn-primary-color) 10%, transparent)',
              '&:hover': {
                backgroundColor: 'color-mix(in srgb, var(--tn-primary-color) 15%, transparent)',
              },
            },
          },
        },
      },

      MuiRadio: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: '8px',
            '&.Mui-checked': {
              color: theme.palette.primary.main,
            },
            '&:hover': {
              backgroundColor: 'color-mix(in srgb, var(--tn-primary-color) 8%, transparent)',
            },
            '&.Mui-focusVisible': {
              outline: `2px dashed ${theme.palette.primary.main}`,
              outlineOffset: '4px',
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
            },
          }),
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: tokens.colors.midGrey,

            '&.Mui-checked': {
              color: theme.palette.primary.main,
            },

            '&:hover': {
              backgroundColor: 'color-mix(in srgb, var(--tn-primary-color) 4%, transparent)',
            },

            '&.Mui-focusVisible': {
              outline: `2px dashed ${theme.palette.primary.main}`,
              outlineOffset: '4px',
            },
          }),
        },
      },

      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            marginRight: 0,
            gap: '8px',
          },
          label: {
            fontFamily: 'Area Normal, Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 400,
            color: tokens.colors.offBlack,
            textTransform: 'none',
          },
        },
      },
    },
  });
};
export default createAppTheme;
